import NcrReportFields from "./NcrReportFields";
import NcrDetailTextField from "./NcrDetailTextField";
import NcrPeopleFields from "./NcrPeopleFields";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Attachments from "layouts/tasks/components/Attachments";
import LoadingButton from "components/LoadingButton";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import * as React from "react";
import { hasNcrTextErrors, hasNcrHeaderErrors, ncrTextFieldProps } from "../ncrTextValidation";

function SectionTitle({ children }) {
  return (
    <Grid item xs={12}>
      <Typography component="h3" variant="h6" sx={{ mt: 0.75, mb: 0 }}>
        {children}
      </Typography>
    </Grid>
  );
}

export default function NcrEditDialog({
  isOpen,
  onSubmit,
  onSubmitDetail,
  onClose,
  data,
  statuses = [],
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [ncr, setNcr] = React.useState(null);
  const [details, setDetails] = React.useState([]);
  const [error, setError] = React.useState("");
  const saving = React.useRef(false);
  const savedDetails = React.useRef(new Map());
  const { label } = useLanguage();

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setError("");
    savedDetails.current = new Map(
      (data?.details || []).map((detail) => [
        detail.id,
        JSON.stringify(detailValues(detail)),
      ]),
    );
    setNcr({
      ...data,
      estimatedClosureDate: data?.estimatedClosureDate
        ? dayjs(data.estimatedClosureDate)
        : null,
    });
    setDetails(
      (data?.details || []).map((detail) => ({
        ...detail,
        statusId: detail.statusId ?? 1,
        attachments: detail.attachments || [],
      })),
    );
  }, [data?.id, isOpen]);

  function handleClose() {
    if (saving.current) return;
    setOpen(false);
    onClose?.();
  }

  function detailValues(detail) {
    return {
      rootCause: detail.rootCause || "",
      correctiveAction: detail.correctiveAction || "",
      remarks: detail.remarks || "",
      statusId: Number(detail.statusId ?? 1),
    };
  }
  function failureMessage(result) {
    return (
      result?.error?.message ||
      result?.error ||
      label("not-saved") ||
      "Unable to save NCR"
    );
  }
  async function persistDetail(detail) {
    const values = detailValues(detail);
    const result = await onSubmitDetail(ncr.id, { id: detail.id, ...values });
    if (!result?.success) throw new Error(failureMessage(result));
    savedDetails.current.set(detail.id, JSON.stringify(values));
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={loading ? undefined : handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();
          if (
            !ncr ||
            saving.current ||
            hasNcrHeaderErrors(ncr) ||
            details.some((detail) => hasNcrTextErrors(detailValues(detail)))
          )
            return;
          saving.current = true;
          setLoading(true);
          setError("");
          try {
            for (const detail of details) {
              if (
                savedDetails.current.get(detail.id) !==
                JSON.stringify(detailValues(detail))
              )
                await persistDetail(detail);
            }
            const header = ncr;
            const result = await onSubmit({
              ...header,
              statusId: Number(ncr.statusId),
              repetitionTime: ncr.repetitionTime === "" || ncr.repetitionTime == null ? null : Number(ncr.repetitionTime),
              estimatedClosureDate: ncr.estimatedClosureDate
                ? ncr.estimatedClosureDate.$d
                : null,
            });
            if (!result?.success) throw new Error(failureMessage(result));
            saving.current = false;
            handleClose();
          } catch (failure) {
            setError(
              failure.message || label("not-saved") || "Unable to save NCR",
            );
          } finally {
            saving.current = false;
            setLoading(false);
          }
        },
      }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography component="h2" variant="h6">
          {label("edit-ncr") || "Edit NCR"}
        </Typography>
        <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5 }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            sx={{ overflowWrap: "anywhere" }}
          >
            {label("code-singular") || "Code"}: {ncr?.code || "—"}
          </Typography>
        </Alert>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Box
          component="fieldset"
          disabled={loading}
          sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
        >
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <SectionTitle>
              {label("report-information") || "Report information"}
            </SectionTitle>
            <Grid item xs={12}>
              <TextField
                id="referenceClause"
                name="referenceClause"
                {...ncrTextFieldProps("referenceClause", ncr?.referenceClause)}
                label={label("reference-clause-singular") || "Reference clause"}
                value={ncr?.referenceClause || ""}
                onChange={(e) => {
                  setNcr({ ...ncr, referenceClause: e.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="branchGroupDescription"
                name="branchGroupDescription"
                {...ncrTextFieldProps(
                  "branchGroupDescription",
                  ncr?.branchGroupDescription,
                )}
                label={
                  label("branch-group-description-singular") ||
                  "Description of branch group"
                }
                value={ncr?.branchGroupDescription || ""}
                onChange={(e) => {
                  setNcr({ ...ncr, branchGroupDescription: e.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={
                    label("estimated-closure-date-singular") ||
                    "Estimated closure date"
                  }
                  value={ncr?.estimatedClosureDate || null}
                  onChange={(value) => {
                    setNcr({ ...ncr, estimatedClosureDate: value });
                  }}
                  sx={{ width: "100%" }}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
            </Grid>

            <NcrReportFields value={ncr} onChange={setNcr} />

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <FormControlLabel
                    label={label("ncr-repeated-label") || "Repeated deviation"}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={!!ncr?.isRepeated}
                        onChange={(e) => {
                          setNcr({ ...ncr, isRepeated: e.target.checked });
                        }}
                      />
                    }
                  />
                  <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                    {label("ncr-repeated-help") ||
                      "Mark this when the deviation has occurred again."}
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    label={label("ncr-reprinted-label") || "Reprinted report"}
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={!!ncr?.isReprinted}
                        onChange={(e) => {
                          setNcr({ ...ncr, isReprinted: e.target.checked });
                        }}
                      />
                    }
                  />
                  <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                    {label("ncr-reprinted-help") ||
                      "Manually indicates a reprinted copy. Printing does not increase the repetition count."}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <SectionTitle>{label("people-heading") || "People"}</SectionTitle>
            <NcrPeopleFields
              isOpen={isOpen}
              auditId={ncr?.auditId || ncr?.audit?.id}
              disabled={loading}
              value={ncr}
              onChange={setNcr}
            />

            <SectionTitle>
              {label("signatures-heading") || "Signatures"}
            </SectionTitle>
            <Grid item xs={12} md={6}>
              <TextField
                id="personInChargeSignature"
                name="personInChargeSignature"
                {...ncrTextFieldProps(
                  "personInChargeSignature",
                  ncr?.personInChargeSignature,
                )}
                label={
                  label("person-in-charge-signature") ||
                  "Person in charge — signature"
                }
                value={ncr?.personInChargeSignature ?? ""}
                onChange={(e) => {
                  setNcr({ ...ncr, personInChargeSignature: e.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="headOfQualitySignature"
                name="headOfQualitySignature"
                {...ncrTextFieldProps(
                  "headOfQualitySignature",
                  ncr?.headOfQualitySignature,
                )}
                label={
                  label("head-of-quality-signature") ||
                  "Head of quality — signature"
                }
                value={ncr?.headOfQualitySignature ?? ""}
                onChange={(e) => {
                  setNcr({ ...ncr, headOfQualitySignature: e.target.value });
                }}
                fullWidth
              />
            </Grid>

            <SectionTitle>{label("kpi-details") || "KPI details"}</SectionTitle>

            {/* Native scrollable textareas avoid auto-size ResizeObserver loops with long drafts. */}
            {details.map((detail, index) => (
              <Grid item xs={12} key={"NCR detail: " + detail.id}>
                <Box
                  component="section"
                  aria-labelledby={`ncr-detail-${detail.id}`}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: { xs: 1, md: 2 },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      id={`ncr-detail-${detail.id}`}
                      component="h4"
                      variant="h6"
                    >
                      DR{detail.rowIndex}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      label={label("ncr-kpi-status") || "KPI status"}
                      value={detail.statusId ?? 1}
                      onChange={(event) =>
                        setDetails((current) => current.map((item) =>
                          item.id === detail.id
                            ? { ...item, statusId: Number(event.target.value) }
                            : item,
                        ))
                      }
                      disabled={loading}
                      sx={{ minWidth: 160, ml: "auto" }}
                    >
                      {statuses.map((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {label("kpi-reference-code-singular") || "Reference code"}:{" "}
                      {detail.kpiReferenceCode || "—"}
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <NcrDetailTextField
                        label={
                          label("non-conformity-description-singular") ||
                          "Non-conformity description"
                        }
                        value={detail.nonConformityDescription || ""}
                        InputProps={{
                          readOnly: true,
                          inputComponent: "textarea",
                        }}
                        helperText={
                          label("from-original-kpi") || "From the original KPI"
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <NcrDetailTextField
                        label={label("root-cause-singular") || "Root cause"}
                        value={detail.rootCause || ""}
                        InputProps={{ inputComponent: "textarea" }}
                        {...ncrTextFieldProps("rootCause", detail.rootCause)}
                        onChange={(e) => {
                          setDetails((details) =>
                            details.map((d, i) =>
                              i == index
                                ? { ...d, rootCause: e.target.value }
                                : d,
                            ),
                          );
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <NcrDetailTextField
                        label={
                          label("corrective-action-singular") ||
                          "Corrective action"
                        }
                        value={detail.correctiveAction || ""}
                        InputProps={{ inputComponent: "textarea" }}
                        {...ncrTextFieldProps(
                          "correctiveAction",
                          detail.correctiveAction,
                        )}
                        onChange={(e) => {
                          setDetails((details) =>
                            details.map((d, i) =>
                              i == index
                                ? { ...d, correctiveAction: e.target.value }
                                : d,
                            ),
                          );
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <NcrDetailTextField
                        label={label("remark-plural")}
                        value={detail.remarks || ""}
                        InputProps={{ inputComponent: "textarea" }}
                        {...ncrTextFieldProps("remarks", detail.remarks)}
                        onChange={(e) => {
                          setDetails((details) =>
                            details.map((d, i) =>
                              i == index
                                ? { ...d, remarks: e.target.value }
                                : d,
                            ),
                          );
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Attachments
                        readOnly
                        attachments={(detail.attachments || []).map(
                          (photo, photoIndex) => ({
                            ...photo,
                            id: `source-${detail.auditKpiId}-${photoIndex}`,
                          }),
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleClose}>
          {label("cancel-button")}
        </Button>
        <LoadingButton
          loading={loading}
          disabled={
            loading ||
            hasNcrHeaderErrors(ncr) ||
            details.some((detail) => hasNcrTextErrors(detailValues(detail)))
          }
          type="submit"
          variant="gradient"
          color="info"
        >
          {label("save-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
